import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SearchIcon, Tag } from "@app/shared/src/components_v2";
import { siteLabel } from "../../helpers/deviceLabels";
import { useFleet } from "../providers/FleetProvider";

/**
 * "Search anything" — trucks by site, key or device name, plus the console's own destinations.
 *
 * Scoped to what the console already holds in memory: the fleet roster and the page list. It
 * answers the question that actually gets typed ("where's TMA-1042?") instantly and offline,
 * rather than round-tripping a server search that doesn't exist yet.
 */

type Hit = { kind: "device" | "page"; label: string; sub?: string; to: string };

const PAGES: Hit[] = [
    { kind: "page", label: "Overview", to: "/overview" },
    { kind: "page", label: "Events", to: "/events" },
    { kind: "page", label: "DVR Requests", to: "/media" },
    { kind: "page", label: "Devices", to: "/devices" },
    { kind: "page", label: "Driver QR Codes", to: "/qr" },
    { kind: "page", label: "Team", to: "/team" },
    { kind: "page", label: "Fleet defaults", sub: "Settings", to: "/settings" },
    { kind: "page", label: "Help Center", to: "/help" },
];

export const SearchPalette: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    // Deliberately not group-scoped. Search is how you find the truck you are thinking of, and
    // "nothing matches TMA-1042" when it plainly exists is the worst answer this box can give.
    const { allDevices: devices } = useFleet();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [cursor, setCursor] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        setQuery("");
        setCursor(0);
        // Focus after paint, or the browser drops it on a freshly mounted node.
        const id = window.setTimeout(() => inputRef.current?.focus(), 0);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => {
            window.clearTimeout(id);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onClose]);

    const hits = useMemo(() => {
        const q = query.trim().toLowerCase();
        // Led by the TMA — that is what gets typed — with the key and the device's own name
        // behind it. Both still match, since the filter below reads the label and the sub together.
        const deviceHits: Hit[] = devices.map((d) => ({
            kind: "device" as const,
            label: siteLabel(d),
            sub: [d.deviceKey, d.name].filter(Boolean).join(" · "),
            to: `/devices/${d.id}`,
        }));
        const all = [...deviceHits, ...PAGES];
        if (!q) return PAGES;
        return all.filter((h) => `${h.label} ${h.sub ?? ""}`.toLowerCase().includes(q)).slice(0, 12);
    }, [devices, query]);

    if (!open) return null;

    const go = (hit: Hit) => {
        onClose();
        navigate(hit.to);
    };

    return (
        <div className="fixed inset-0 z-50">
            <button type="button" aria-label="Close search" onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
            <div className="absolute left-1/2 top-[14vh] w-[min(620px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-[16px] border border-[var(--line-2)] bg-[var(--surface)] shadow-[0_40px_90px_rgba(0,0,0,.5)]">
                <div className="flex items-center gap-3 border-b border-[var(--line)] px-5 py-4 text-[var(--faint)]">
                    <SearchIcon size={19} />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setCursor(0);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                                e.preventDefault();
                                setCursor((c) => Math.max(0, Math.min(hits.length - 1, c + (e.key === "ArrowDown" ? 1 : -1))));
                            } else if (e.key === "Enter" && hits[cursor]) {
                                go(hits[cursor]);
                            }
                        }}
                        placeholder="Search trucks, keys, sites and pages…"
                        className="w-full bg-transparent text-[15px] text-[var(--text)] outline-none placeholder:text-[var(--faint)]"
                    />
                </div>
                <div className="max-h-[50vh] overflow-y-auto p-2">
                    {hits.length === 0 ? (
                        <div className="px-3 py-6 text-center text-[13px] text-[var(--muted)]">Nothing matches “{query}”.</div>
                    ) : (
                        hits.map((h, i) => (
                            <button
                                key={`${h.kind}-${h.to}-${h.label}`}
                                type="button"
                                onClick={() => go(h)}
                                onMouseEnter={() => setCursor(i)}
                                className={`flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left ${
                                    i === cursor ? "bg-[var(--surface-2)]" : ""
                                }`}
                            >
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-[13.5px] font-semibold">{h.label}</span>
                                    {h.sub && <span className="block truncate text-[11.5px] text-[var(--muted)]">{h.sub}</span>}
                                </span>
                                <Tag tone={h.kind === "device" ? "accent" : "mute"}>{h.kind === "device" ? "Truck" : "Page"}</Tag>
                            </button>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
